import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { userApi } from "./userApis";

const pause = (duration) => {
  return new Promise((resolve) => setTimeout(resolve, duration));
};

const dayoffApi = createApi({
  reducerPath: "dayoff",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BASE_ENDPOINT}api`,
    prepareHeaders: (headers, { getState }) => {
      const token = localStorage.getItem("access-token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
    },
    fetchFn: async (...args) => {
      await pause(1000);
      return fetch(...args);
    },
  }),
  endpoints(builder) {
    return {
      fetchPermissionList: builder.query({
        providesTags: (result, error, arg) => ["permission-personels"],
        query: ({ page, limit, searchText, date }) => {
          return {
            url: `dayoff/permission-grid?page=${page}&limit=${limit}&searchText=${searchText}&date=${date}`,
            method: "GET",
          };
        },
      }),
      fetchDayoffList: builder.query({
        providesTags: (result) => ["dayoff-list"],
        query: ({ page, limit }) => {
          return {
            url: `dayoff/dayoff-grid?page=${page}&limit=${limit}`,
            method: "GET",
          };
        },
      }),
      fetchGetDayoff: builder.query({
        providesTags: (result) => [
          { type: "dayoff-item", id: result?.data?.id },
        ],
        query: (id) => {
          return {
            url: `dayoff/${id}`,
            method: "GET",
          };
        },
      }),
      postDayoff: builder.mutation({
        invalidatesTags: (result, error, arg) => [
          "dayoff-list",
          "personel-rights",
          { type: "dayoff-item", id: arg.id },
        ],
        query: ({ dayoff, id }) => {
          return {
            url: id === 0 ? "dayoff/create" : "dayoff/update",
            method: "POST",
            body: dayoff,
          };
        },
      }),
      deleteDayoff: builder.mutation({
        invalidatesTags: (result, error, arg) => [
          "dayoff-list",
          "personel-rights",
          "report",
          { type: "dayoff-item", id: arg.id },
        ],
        query: (id) => {
          return {
            url: `dayoff?dayoffId=${id}`,
            method: "DELETE",
          };
        },
      }),
      approveDayoff: builder.mutation({
        invalidatesTags: (result, error, arg) => [
          "personel-rights",
          "permission-personels",
          "report",
          { type: "permission-personel", id: arg.id },
        ],
        query: (approveModel) => {
          return {
            url: "dayoff/approve",
            method: "POST",
            body: approveModel,
          };
        },
        onCacheEntryAdded: async (args, { dispatch, queryFulfilled }) => {
          await queryFulfilled;
          dispatch(userApi.util.invalidateTags(["main-informations"]));
        },
      }),
      fetchPersonelRights: builder.query({
        providesTags: (result, error, arg) => ["personel-rights"],
        query: ({ page, limit, period, userId }) => {
          return {
            url:
              userId === undefined
                ? `userdayoffright?page=${page}&limit=${limit}&period=${period}`
                : `userdayoffright?page=${page}&limit=${limit}&period=${period}&userId=${userId}`,
            method: "GET",
          };
        },
      }),
      fetchGetLastPeriodOfUser: builder.query({
        providesTags: (result, error, arg) => ["personel-rights"],
        query: () => {
          return {
            url: `userdayoffright/last-period`,
            method: "GET",
          };
        },
      }),
      fetchPersonelPeriods: builder.query({
        providesTags: (result, error, arg) => ["personel-periods"],
        query: ({ userId }) => {
          return {
            url:
              userId === undefined
                ? "userdayoffright/periods"
                : `userdayoffright/periods?userId=${userId}`,
            method: "GET",
          };
        },
      }),
      fetchDayoffDocuments: builder.query({
        providesTags: () => ["dayoff-documents"],
        query: (id) => {
          return {
            url: `dayoff/documents?id=${id}`,
            method: "GET",
          };
        },
      }),
      renewDayoff: builder.mutation({
        invalidatesTags: (result, error, arg) => ["personel-rights"],
        query: () => {
          return {
            url: "userdayoffright/renew",
            method: "POST",
          };
        },
      }),
      enterBulkDayoff: builder.mutation({
        invalidatesTags: (result, error, arg) => [
          "personel-rights",
          "permission-personels",
          "report",
        ],
        query: (bulkModel) => {
          return {
            url: "dayoff/enterbulk",
            method: "POST",
            body: bulkModel,
          };
        },
        onCacheEntryAdded: async (args, { dispatch, queryFulfilled }) => {
          await queryFulfilled;
          dispatch(userApi.util.invalidateTags(["main-informations"]));
        },
      }),
      fetchReport: builder.query({
        query: ({ firstDate, secondDate }) => ({
          url: `dayoff/report?firstDate=${firstDate}&secondDate=${secondDate}`,
          method: "GET",
        }),
      }),
      fetchExportToExcel: builder.query({
        query: ({ firstDate, secondDate }) => ({
          url: `dayoff/export-excel?firstDate=${firstDate}&secondDate=${secondDate}`,
          method: "GET",
        }),
      }),
      fetchSystemForm: builder.query({
        query: (id) => ({
          url: `dayoff/system-form?dayoffId=${id}`,
          method: "GET",
        }),
      }),
    };
  },
});

export const {
  useFetchPermissionListQuery,
  useFetchDayoffListQuery,
  useFetchGetDayoffQuery,
  usePostDayoffMutation,
  useDeleteDayoffMutation,
  useApproveDayoffMutation,
  useFetchPersonelRightsQuery,
  useFetchDayoffDocumentsQuery,
  useFetchPersonelPeriodsQuery,
  useRenewDayoffMutation,
  useEnterBulkDayoffMutation,
  useLazyFetchReportQuery,
  useLazyFetchExportToExcelQuery,
  useFetchGetLastPeriodOfUserQuery,
  useLazyFetchSystemFormQuery,
} = dayoffApi;
export { dayoffApi };
